import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Tag,
  Spin,
  Alert,
  Space,
  Button,
  Modal,
  message,
} from "antd";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../../../components/AgGridWithColumnState";
import {
  RollbackOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  getAllInvoiceHistory,
  rollbackInvoiceHistory,
  getAllCreditHistory,
  rollbackCreditHistory,
} from "../../../api/accounting";
import useGlobalFilters from "../../../../../hooks/useGlobalFilters";
import useDisplayCurrency from "../../../../../hooks/useDisplayCurrency";
import {
  getCachedData,
  setCachedData,
  invalidateCacheByPrefix,
  CacheTTL,
} from "../../../context/AccountingDataCache";


const BillHistory = ({ firstBrand = null, selectedBrands = [] }) => {
  // Global currency display - refreshKey changes when currency changes, forcing grid refresh
  const { refreshKey: currencyRefreshKey } = useDisplayCurrency();
  const { selectedBrands: globalSelectedBrands } = useGlobalFilters();
  const [error, setError] = useState(null);
  const [dateRange] = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);

  // Use selectedBrands if available, otherwise fall back to firstBrand
  const brandsToUse = useMemo(() => {
    if (globalSelectedBrands && globalSelectedBrands.length > 0) {
      return globalSelectedBrands;
    }
    if (selectedBrands && selectedBrands.length > 0) {
      return selectedBrands;
    }
    return firstBrand ? [firstBrand] : [];
  }, [globalSelectedBrands, selectedBrands, firstBrand]);

  // Create a stable key from brandsToUse to prevent unnecessary re-fetches
  const brandsKey = useMemo(() => {
    if (!brandsToUse || brandsToUse.length === 0) return "";
    return brandsToUse
      .map((b) => b.id)
      .sort()
      .join(",");
  }, [brandsToUse]);

  // Create a stable key for date range
  const dateRangeKey = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) return "";
    return `${dateRange[0]?.format("YYYY-MM-DD")}_${dateRange[1]?.format(
      "YYYY-MM-DD"
    )}`;
  }, [dateRange]);

  // Global cache key
  const cacheKey = useMemo(() => {
    if (!brandsKey) return null;
    return `bill_history_${brandsKey}_${dateRangeKey || "all"}`;
  }, [brandsKey, dateRangeKey]);

  // Check global cache for initial data - instant restore on navigation
  const initialCachedData = useMemo(() => {
    if (!cacheKey) return null;
    return getCachedData(cacheKey);
  }, [cacheKey]);

  // Initialize state from cache if available
  const [historyData, setHistoryData] = useState(() => initialCachedData || []);
  const [loading, setLoading] = useState(() => !initialCachedData);

  const fetchInFlightRef = useRef(false);
  const lastCompletedFetchKeyRef = useRef(initialCachedData ? cacheKey : null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: initialCachedData?.length || 0,
    totalPages: 0,
  });

  // Fetch all bill history for selected brand (single fetch, client-side pagination)
  const fetchAllHistory = useCallback(
    async (force = false) => {
      if (!brandsToUse || brandsToUse.length === 0 || !cacheKey) {
        setHistoryData([]);
        setPagination((prev) => ({ ...prev, current: 1, total: 0, totalPages: 0 }));
        lastCompletedFetchKeyRef.current = null;
        return;
      }

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
          setHistoryData(cachedData);
          setPagination((prev) => ({ ...prev, total: cachedData.length }));
          setLoading(false);
          lastCompletedFetchKeyRef.current = cacheKey;
          return;
        }
      }

      // Already completed this fetch
      if (!force && lastCompletedFetchKeyRef.current === cacheKey) {
        setLoading(false);
        return;
      }

      // Already fetching
      if (fetchInFlightRef.current) {
        return;
      }

      fetchInFlightRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const brandIds = brandsToUse
          .map((b) => b.id)
          .filter((id) => id !== undefined && id !== null)
          .sort((a, b) => a - b)
          .join(",");

        const dateKey = dateRange && dateRange[0] && dateRange[1]
          ? `${dateRange[0].format("YYYY-MM-DD")}_${dateRange[1].format("YYYY-MM-DD")}`
          : "none";

        const baseFilters = {
          brand: brandIds,
          page: 1,
          page_size: 500,
        };

        if (dateKey !== "none") {
          const [start, end] = dateKey.split("_");
          baseFilters.start_date = start;
          baseFilters.end_date = end;
        }

        // Fetch both invoice history and credit history in parallel
        const [invoiceResponse, creditResponse] = await Promise.all([
          getAllInvoiceHistory({ ...baseFilters, invoice_type: "bill" }),
          getAllCreditHistory({ ...baseFilters, credit_type: "bill" }),
        ]);

        // Transform credit history to match invoice history format
        const creditHistory = (creditResponse.results || []).map((credit) => ({
          ...credit,
          number: credit.number || `CR-${credit.id}`,
          invoice_type: "credit",
          is_credit: true,
        }));

        // Merge and sort by history_date descending
        const mergedHistory = [
          ...(invoiceResponse.results || []),
          ...creditHistory,
        ].sort((a, b) => new Date(b.history_date || 0) - new Date(a.history_date || 0));

        setHistoryData(mergedHistory);
        setPagination((prev) => ({
          ...prev,
          total: mergedHistory.length,
          totalPages: Math.ceil(mergedHistory.length / prev.pageSize),
        }));

        // Cache in GLOBAL cache (5 minute TTL)
        setCachedData(cacheKey, mergedHistory, CacheTTL.MEDIUM);
        lastCompletedFetchKeyRef.current = cacheKey;
      } catch {
        setError("Failed to load bill history. Please try again.");
      } finally {
        setLoading(false);
        fetchInFlightRef.current = false;
      }
    },
    [brandsToUse, dateRange, cacheKey]
  );

  // Fetch bill history when brands or date range changes
  useEffect(() => {
    // Skip if no brands selected
    if (!brandsKey || brandsKey === "" || !cacheKey) {
      setHistoryData([]);
      return;
    }

    // Already fetched for this key
    if (lastCompletedFetchKeyRef.current === cacheKey) {
      return;
    }

    // Check GLOBAL cache first before fetching
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      setHistoryData(cachedData);
      setPagination((prev) => ({ ...prev, total: cachedData.length }));
      setLoading(false);
      lastCompletedFetchKeyRef.current = cacheKey;
      return;
    }

    // Add delay to let main data requests complete first
    // History is secondary - user needs to see bills/invoices first
    const timeoutId = setTimeout(() => {
      fetchAllHistory().catch(() => undefined);
    }, 1500); // 1.5 second delay to let main data load first
    return () => clearTimeout(timeoutId);
  }, [cacheKey, brandsKey, fetchAllHistory]);

  // Handle rollback to a previous version
  const handleRollback = useCallback((historyId, recordDate, isCredit = false) => {
    Modal.confirm({
      title: "Confirm Rollback",
      content: `Are you sure you want to roll back this ${
        isCredit ? "credit note" : "bill"
      } to the version from ${moment(recordDate).format(
        "YYYY-MM-DD HH:mm:ss"
      )}?`,
      okText: "Yes, Roll Back",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setRollbackLoading(true);
          const result = isCredit
            ? await rollbackCreditHistory(historyId, recordDate)
            : await rollbackInvoiceHistory(historyId);
          message.success(
            result.message ||
              `${isCredit ? "Credit note" : "Bill"} rolled back successfully`
          );
          // Refresh the history data (force refresh to get updated data)
          invalidateCacheByPrefix("bill_history_");
          fetchAllHistory(true);
        } catch (err) {
          message.error(
            `Failed to roll back ${isCredit ? "credit note" : "bill"}: ` +
              (err.response?.data?.error || err.message)
          );
        } finally {
          setRollbackLoading(false);
        }
      },
    });
  }, [fetchAllHistory]);

  // Format history type for display
  const getHistoryTypeDisplay = (type, record) => {
    // Check if this is a rollback operation
    const isRollback = record?.field_changes?.rollback || 
                      (record?.history_change_reason && 
                       record.history_change_reason.toLowerCase().includes('rollback'));
    
    if (isRollback) {
      return <Tag color="orange">Rolled back</Tag>;
    }
    
    switch (type) {
      case "+":
        return <Tag color="green">Created</Tag>;
      case "~":
        return <Tag color="blue">Updated</Tag>;
      case "-":
        return <Tag color="red">Deleted</Tag>;
      default:
        return <Tag>{type}</Tag>;
    }
  };

  // Helper function to format field names for display
  const formatFieldName = (fieldName) => {
    const fieldNameMap = {
      invoice_type: "Bill Type",
      number: "Bill Number",
      partner: "Partner ID",
      partner_name: "Partner",
      issue_date: "Issue Date",
      status: "Status",
      currency: "Currency",
      subtotal: "Subtotal",
      discount_amount: "Discount",
      total_amount: "Total Amount",
      tax_rate: "Tax Rate",
      paid_amount: "Paid Amount",
      notes: "Notes",
      brand: "Brand ID",
    };
    return (
      fieldNameMap[fieldName] ||
      fieldName.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  // Helper function to format field values for display
  const formatFieldValue = (fieldName, value) => {
    if (value === null || value === undefined) return "-";

    // Format monetary values
    if (
      ["subtotal", "discount_amount", "total_amount", "paid_amount"].includes(
        fieldName
      )
    ) {
      return `$${parseFloat(value).toFixed(2)}`;
    }

    // Format tax rate
    if (fieldName === "tax_rate") {
      return `${parseFloat(value).toFixed(2)}%`;
    }

    // Format dates
    if (fieldName === "issue_date") {
      return moment(value).format("YYYY-MM-DD");
    }

    return value.toString();
  };

  // Render simplified field changes
  const renderFieldChanges = useCallback((record) => {
    if (
      !record.field_changes ||
      Object.keys(record.field_changes).length === 0
    ) {
      return "-";
    }

    // For credits, show field changes even if created/deleted
    const isCredit = record.is_credit;
    const isCreated = record.field_changes.created;
    const isDeleted = record.field_changes.deleted;
    const isRollback = record.field_changes.rollback || 
                      (record.history_change_reason && 
                       record.history_change_reason.toLowerCase().includes('rollback'));

    // Extract simple change labels with values for monetary fields
    const changeParts = [];
    
    // Check for rollback first
    if (isRollback) {
      return (
        <span style={{ color: "#fa8c16", fontWeight: "500" }}>
          Rolled back
        </span>
      );
    }
    
    // Add created/deleted indicator if present, but don't return early for credits
    if (isCreated && !isCredit) {
      return (
        <span style={{ color: "#52c41a", fontWeight: "500" }}>
          Bill Created
        </span>
      );
    }

    if (isDeleted && !isCredit) {
      return (
        <span style={{ color: "#ff4d4f", fontWeight: "500" }}>
          Bill Deleted
        </span>
      );
    }

    // For credits, show created/deleted as part of the changes
    if (isCreated && isCredit) {
      changeParts.push(
        <span key="created" style={{ color: "#52c41a", fontWeight: "500" }}>
          Credit Created
        </span>
      );
    }

    if (isDeleted && isCredit) {
      changeParts.push(
        <span key="deleted" style={{ color: "#ff4d4f", fontWeight: "500" }}>
          Credit Deleted
        </span>
      );
    }

    Object.entries(record.field_changes).forEach(([field, change]) => {
      // Skip created/deleted flags as we've already handled them
      if (field === "created" || field === "deleted") {
        return;
      }
      if (field === "status") {
        const oldStatus = change.old?.toLowerCase() || "";
        const newStatus = change.new?.toLowerCase() || "";
        changeParts.push(
          <span key={field}>
            <span style={{ color: "#ff4d4f" }}>{oldStatus}</span>
            {" → "}
            <span style={{ color: "#52c41a", fontWeight: "500" }}>
              {newStatus}
            </span>
          </span>
        );
      } else if (field === "paid_amount") {
        const oldValue = formatFieldValue(field, change.old);
        const newValue = formatFieldValue(field, change.new);
        changeParts.push(
          <span key={field}>
            Paid: <span style={{ color: "#ff4d4f" }}>{oldValue}</span>
            {" → "}
            <span style={{ color: "#52c41a", fontWeight: "500" }}>
              {newValue}
            </span>
          </span>
        );
      } else if (field === "total_amount") {
        const oldValue = formatFieldValue(field, change.old);
        const newValue = formatFieldValue(field, change.new);
        changeParts.push(
          <span key={field}>
            Total: <span style={{ color: "#ff4d4f" }}>{oldValue}</span>
            {" → "}
            <span style={{ color: "#52c41a", fontWeight: "500" }}>
              {newValue}
            </span>
          </span>
        );
      } else if (field === "subtotal") {
        const oldValue = formatFieldValue(field, change.old);
        const newValue = formatFieldValue(field, change.new);
        changeParts.push(
          <span key={field}>
            Subtotal: <span style={{ color: "#ff4d4f" }}>{oldValue}</span>
            {" → "}
            <span style={{ color: "#52c41a", fontWeight: "500" }}>
              {newValue}
            </span>
          </span>
        );
      } else if (field === "discount_amount") {
        const oldValue = formatFieldValue(field, change.old);
        const newValue = formatFieldValue(field, change.new);
        changeParts.push(
          <span key={field}>
            Discount: <span style={{ color: "#ff4d4f" }}>{oldValue}</span>
            {" → "}
            <span style={{ color: "#52c41a", fontWeight: "500" }}>
              {newValue}
            </span>
          </span>
        );
      } else if (
        field === "tax_rate" ||
        field === "issue_date" ||
        field === "due_date"
      ) {
        const oldValue = formatFieldValue(field, change.old);
        const newValue = formatFieldValue(field, change.new);
        changeParts.push(
          <span key={field}>
            {formatFieldName(field)}:{" "}
            <span style={{ color: "#ff4d4f" }}>{oldValue}</span>
            {" → "}
            <span style={{ color: "#52c41a", fontWeight: "500" }}>
              {newValue}
            </span>
          </span>
        );
      } else if (field === "credit_type") {
        const oldValue = change.old || "";
        const newValue = change.new || "";
        changeParts.push(
          <span key={field}>
            Type: <span style={{ color: "#ff4d4f" }}>{oldValue}</span>
            {" → "}
            <span style={{ color: "#52c41a", fontWeight: "500" }}>
              {newValue}
            </span>
          </span>
        );
      } else if (field === "approval_status") {
        const oldStatus = change.old || "";
        const newStatus = change.new || "";
        const statusColors = {
          draft: "#faad14",
          pending_senior_approval: "#1890ff",
          pending_management_approval: "#722ed1",
          approved: "#52c41a",
          rejected: "#ff4d4f",
        };
        changeParts.push(
          <span key={field}>
            Approval: <span style={{ color: statusColors[oldStatus] || "#666" }}>{oldStatus}</span>
            {" → "}
            <span style={{ color: statusColors[newStatus] || "#52c41a", fontWeight: "500" }}>
              {newStatus}
            </span>
          </span>
        );
      } else if (field === "rejection_reason") {
        const reason = change.new || "";
        if (reason) {
          changeParts.push(
            <span key={field} style={{ color: "#ff4d4f" }}>
              Rejection Reason: {reason}
            </span>
          );
        }
      } else if (field === "senior_approver") {
        const approver = change.new || "";
        if (approver) {
          changeParts.push(
            <span key={field} style={{ color: "#52c41a" }}>
              Reviewed by: {approver}
            </span>
          );
        }
      } else if (field === "management_approver") {
        const approver = change.new || "";
        if (approver) {
          changeParts.push(
            <span key={field} style={{ color: "#52c41a" }}>
              Approved by Management: {approver}
            </span>
          );
        }
      } else if (field === "rejected_by") {
        const rejector = change.new || "";
        if (rejector) {
          changeParts.push(
            <span key={field} style={{ color: "#ff4d4f" }}>
              Rejected by: {rejector}
            </span>
          );
        }
      } else if (field === "allocated_amount" || field === "allocation_amount") {
        const oldValue = formatFieldValue("paid_amount", change.old);
        const newValue = formatFieldValue("paid_amount", change.new);
        changeParts.push(
          <span key={field}>
            Allocated: <span style={{ color: "#ff4d4f" }}>{oldValue}</span>
            {" → "}
            <span style={{ color: "#52c41a", fontWeight: "500" }}>
              {newValue}
            </span>
          </span>
        );
      } else if (field === "overpayment_allocation" || field === "credit_allocation" || field === "cash_allocation") {
        const allocationType = field.replace("_allocation", "").replace("_", " ");
        changeParts.push(
          <span key={field} style={{ color: "#1890ff", fontWeight: "500" }}>
            {allocationType.charAt(0).toUpperCase() + allocationType.slice(1)} allocation applied
          </span>
        );
      } else if (field === "partner" || field === "number") {
        changeParts.push(
          <span key={field} style={{ color: "#1890ff" }}>
            {formatFieldName(field)}
          </span>
        );
      } else if (field === "currency" || field === "notes") {
        // Handle other fields that might change
        const oldValue = change.old || "-";
        const newValue = change.new || "-";
        changeParts.push(
          <span key={field}>
            {formatFieldName(field)}:{" "}
            <span style={{ color: "#ff4d4f" }}>{oldValue}</span>
            {" → "}
            <span style={{ color: "#52c41a", fontWeight: "500" }}>
              {newValue}
            </span>
          </span>
        );
      }
    });

    if (changeParts.length === 0) {
      return "-";
    }

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
        }}
      >
        {changeParts.map((part, index) => (
          <React.Fragment key={index}>
            {part}
            {index < changeParts.length - 1 && (
              <span style={{ color: "#d9d9d9" }}>|</span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }, []); // No dependencies - pure function

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Date",
        field: "history_date",
        width: 180,
        sortable: true,
        filter: "agDateColumnFilter",
        cellRenderer: (params) =>
          moment(params.value).format("YYYY-MM-DD HH:mm:ss"),
      },
      {
        headerName: "Type",
        field: "is_credit",
        width: 100,
        sortable: true,
        filter: "agSetColumnFilter",
        filterParams: {
          values: [true, false],
          valueFormatter: (params) => (params.value ? "Credit" : "Bill"),
        },
        cellRenderer: (params) => (
          <Tag color={params.value ? "blue" : "green"}>
            {params.value ? "Credit" : "Bill"}
          </Tag>
        ),
      },
      {
        headerName: "Bill/Credit #",
        field: "number",
        width: 150,
        sortable: true,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Action",
        field: "history_type",
        width: 120,
        sortable: true,
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["+", "~", "-"],
          valueFormatter: (params) => {
            switch (params.value) {
              case "+":
                return "Created";
              case "~":
                return "Updated";
              case "-":
                return "Deleted";
              default:
                return params.value;
            }
          },
        },
        cellRenderer: (params) => getHistoryTypeDisplay(params.value, params.data),
      },
      {
        headerName: "User",
        field: "history_username",
        width: 200,
        sortable: true,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Changes",
        field: "field_changes",
        width: 400,
        sortable: false,
        filter: false,
        cellRenderer: (params) => renderFieldChanges(params.data),
      },
      {
        headerName: "Actions",
        field: "actions",
        width: 120,
        sortable: false,
        filter: false,
        pinned: "right",
        cellRenderer: (params) => (
          <Button
            type="primary"
            icon={<RollbackOutlined />}
            size="small"
            onClick={() =>
              handleRollback(
                params.data.history_id,
                params.data.history_date,
                params.data.is_credit
              )
            }
            loading={rollbackLoading}
            disabled={params.data.history_type === "-"}
          >
            Rollback
          </Button>
        ),
      },
    ],
    [rollbackLoading, handleRollback, renderFieldChanges]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressMovable: false,
    }),
    []
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Space>
      </div>

      <Spin spinning={loading}>
        <div className="ag-theme-alpine" style={{ height: 600, width: "100%" }}>
          <AgGridWithColumnState
            key={`bill-history-${currencyRefreshKey}`}
            tableId="bill-history"
            columnDefs={columnDefs}
            rowData={historyData}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={pagination.pageSize}
            paginationPageSizeSelector={[10, 25, 50, 100]}
            onPaginationChanged={(params) => {
              // Client-side pagination - only update if values changed to prevent infinite loop
              if (params.api) {
                const newPage = params.api.paginationGetCurrentPage() + 1;
                const newPageSize = params.api.paginationGetPageSize();
                setPagination((prev) => {
                  if (prev.current === newPage && prev.pageSize === newPageSize) {
                    return prev; // No change - don't trigger re-render
                  }
                  return { ...prev, current: newPage, pageSize: newPageSize };
                });
              }
            }}
            domLayout="normal"
            enableCellTextSelection={true}
            ensureDomOrder={true}
            animateRows={false}
            rowBuffer={10}
            suppressScrollOnNewData={true}
            initialState={{
              sort: {
                sortModel: [{ colId: "history_date", sort: "desc" }],
              },
            }}
          />
        </div>
      </Spin>
    </div>
  );
};

export default BillHistory;
